import { useState, useRef, useEffect, useCallback } from "react";

/* ─── Slide data ─────────────────────────────────────────────────────── */
const SLIDES = [
  {
    id: 1,
    title: "Triple Differential Lock - Front",
    description: "Provides unyielding traction to front wheels.",
    poster:    "https://mgmotor.scene7.com/is/image/mgmotor/poweredbymhubfront-img-001?fmt=webp-alpha&resMode=bisharp&fit=constrain&qlt=90&wid=1920",
    posterMob: "https://mgmotor.scene7.com/is/image/mgmotor/poweredbymhubfront-img-001?fmt=webp-alpha&resMode=bisharp&fit=constrain&qlt=90&wid=720",
    vidDesk:   "https://mgmotor.scene7.com/is/content/mgmotor/poweredbymhubfront-vid-001",
    vidMob:    "https://mgmotor.scene7.com/is/content/mgmotor/poweredbymhubfront-vid-001-portrait",
  },
  {
    id: 2,
    title: "Triple Differential Lock - Centre",
    description: "Locks front and rear axles to deliver balanced power.",
    poster:    "https://mgmotor.scene7.com/is/image/mgmotor/poweredbymhubcenter-img-002?fmt=webp-alpha&resMode=bisharp&fit=constrain&qlt=90&wid=1920",
    posterMob: "https://mgmotor.scene7.com/is/image/mgmotor/poweredbymhubcenter-img-002?fmt=webp-alpha&resMode=bisharp&fit=constrain&qlt=90&wid=720",
    vidDesk:   "https://mgmotor.scene7.com/is/content/mgmotor/poweredbymhubcenter-vid-002",
    vidMob:    "https://mgmotor.scene7.com/is/content/mgmotor/poweredbymhubcenter-vid-002-portrait",
  },
  {
    id: 3,
    title: "Triple Differential Lock - Rear",
    description: "Maximizes rear grip to push through shifty surfaces.",
    poster:    "https://mgmotor.scene7.com/is/image/mgmotor/poweredbymhubrear-img-003?fmt=webp-alpha&resMode=bisharp&fit=constrain&qlt=90&wid=1920",
    posterMob: "https://mgmotor.scene7.com/is/image/mgmotor/poweredbymhubrear-img-003?fmt=webp-alpha&resMode=bisharp&fit=constrain&qlt=90&wid=720",
    vidDesk:   "https://mgmotor.scene7.com/is/content/mgmotor/poweredbymhubrear-vid-003",
    vidMob:    "https://mgmotor.scene7.com/is/content/mgmotor/poweredbymhubrear-vid-003-portrait",
  },
  {
    id: 4,
    title: "M-Crawl",
    description: "Intelligently manages throttle and braking.",
    poster:    "https://mgmotor.scene7.com/is/image/mgmotor/poweredbymhubmcrawl-img-004?fmt=webp-alpha&resMode=bisharp&fit=constrain&qlt=90&wid=1920",
    posterMob: "https://mgmotor.scene7.com/is/image/mgmotor/poweredbymhubmcrawl-img-004?fmt=webp-alpha&resMode=bisharp&fit=constrain&qlt=90&wid=720",
    vidDesk:   "https://mgmotor.scene7.com/is/content/mgmotor/poweredbymhubmcrawl-vid-004",
    vidMob:    "https://mgmotor.scene7.com/is/content/mgmotor/poweredbymhubmcrawl-vid-004-portrait",
  },
  {
    id: 5,
    title: "810mm Water Wading Capacity",
    description: "Drive through flooded areas or rivers.",
    poster:    "https://mgmotor.scene7.com/is/image/mgmotor/poweredbymhubwaterwading-img-005?fmt=webp-alpha&resMode=bisharp&fit=constrain&qlt=90&wid=1920",
    posterMob: "https://mgmotor.scene7.com/is/image/mgmotor/poweredbymhubwaterwading-img-005?fmt=webp-alpha&resMode=bisharp&fit=constrain&qlt=90&wid=720",
    vidDesk:   "https://mgmotor.scene7.com/is/content/mgmotor/poweredbymhubwaterwading-vid-005",
    vidMob:    "https://mgmotor.scene7.com/is/content/mgmotor/poweredbymhubwaterwading-vid-005-portrait",
  },
];

const N   = SLIDES.length;   // 5
const GAP = 16;               // original owl margin-right

/*
  INFINITE LOOP STRATEGY — same as owl-carousel:
  We clone `vc` slides at each end.
  Track order: [clones-of-end | real slides | clones-of-start]
  Real slides start at index `vc` in the track.
  When we slide past the last real slide → instantly jump to the first real slide (no anim).
  When we slide before the first real slide → instantly jump to the last real slide (no anim).
*/

/* ─── Video card ─────────────────────────────────────────────────────── */
function Vid({ slide, mob }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => (e.isIntersecting ? el.play().catch(() => {}) : el.pause()),
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <video
      ref={ref} autoPlay playsInline muted loop preload="auto"
      poster={mob ? slide.posterMob : slide.poster}
      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
    >
      <source src={slide.vidDesk} type="video/mp4" media="(min-width:991px)" />
      <source src={slide.vidMob}  type="video/mp4" media="(min-width:320px)" />
    </video>
  );
}

/* ─── Arrow button ───────────────────────────────────────────────────── */
function Arr({ dir, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      aria-label={dir === "prev" ? "Previous" : "Next"}
      style={{
        width: 44, height: 44, borderRadius: "50%",
        border: "1.5px solid rgba(170,170,170,0.5)",
        background: hov ? "#fff" : "rgba(255,255,255,0.94)",
        boxShadow: hov ? "0 4px 20px rgba(0,0,0,0.2)" : "0 2px 8px rgba(0,0,0,0.12)",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer",
        transition: "box-shadow 0.18s, background 0.18s",
        padding: 0, outline: "none", flexShrink: 0,
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="#111" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
        {dir === "prev"
          ? <polyline points="15 18 9 12 15 6" />
          : <polyline points="9 18 15 12 9 6" />}
      </svg>
    </button>
  );
}

/* ─── Main ───────────────────────────────────────────────────────────── */
export default function MHubSection() {
  const secRef   = useRef(null);
  const timerRef = useRef(null);

  /* Layout */
  const [lay, setLay] = useState({ cw: 0, vc: 2, pk: 0, mob: false });

  /*
    trackIdx = position in the EXTENDED track (clones-of-end + reals + clones-of-start).
    Real slides live at positions vc … vc+N-1.
    We start at vc (first real slide).
  */
  const [trackIdx, setTrackIdx] = useState(0); // will be set to vc after calc
  const [animated, setAnimated] = useState(true);

  /* Drag */
  const drag = useRef({ on: false, x0: 0, x1: 0 });
  const [dragging, setDragging] = useState(false);

  /* ── Layout ─────────────────────────────────────────────────────── */
  const calc = useCallback(() => {
    if (!secRef.current) return;
    const W  = secRef.current.offsetWidth;
    const vw = window.innerWidth;
    let vc, pk, cw;

    if (vw < 768) {
      vc = 1;
      pk = Math.round(W * 0.06);
      cw = Math.round(W * 0.84);
    } else if (vw < 1200) {
      vc = 2;
      pk = Math.round(W * 0.045);
      cw = Math.round((W - pk * 2 - GAP) / 2);
    } else {
      vc = 2;
      pk = Math.round(W * 0.042);
      cw = Math.round((W - pk * 2 - GAP) / 2);
    }

    setLay(prev => {
      // If vc changed, reset trackIdx to first real slide
      if (prev.vc !== vc) {
        setTrackIdx(vc);
      }
      return { cw, vc, pk, mob: vw < 768 };
    });
  }, []);

  useEffect(() => {
    calc();
    const ro = new ResizeObserver(calc);
    if (secRef.current) ro.observe(secRef.current);
    return () => ro.disconnect();
  }, [calc]);

  /* Init trackIdx once vc is known */
  useEffect(() => {
    if (lay.vc > 0 && trackIdx === 0) {
      setAnimated(false);
      setTrackIdx(lay.vc);
    }
  }, [lay.vc]);

  const { cw, vc, pk, mob } = lay;

  /*
    Build the extended slides array:
    [ ...last vc slides (clones) | ...all N real slides | ...first vc slides (clones) ]
    Total length = N + 2*vc
  */
  const extended = vc > 0
    ? [
        ...SLIDES.slice(N - vc),        // clones of end (at front)
        ...SLIDES,                        // real slides
        ...SLIDES.slice(0, vc),           // clones of start (at back)
      ]
    : SLIDES;

  /* trackIdx → real slide index (0-based) */
  const realIdx = ((trackIdx - vc) % N + N) % N;

  /* Dot count = ceil(N / vc) — matches original owl 3 dots */
  const dotCount = Math.ceil(N / vc);
  const activeDot = Math.floor(realIdx / vc) % dotCount;

  /* translateX */
  const tx = pk - trackIdx * (cw + GAP);

  /* Video area height for arrow centering */
  const videoH = cw > 0 ? Math.round(cw * (9.7 / 16)) : 0;

  /* ── Navigate ──────────────────────────────────────────────────── */
  const goTo = useCallback((nextTrackIdx, withAnim = true) => {
    setAnimated(withAnim);
    setTrackIdx(nextTrackIdx);
  }, []);

  const next = useCallback(() => goTo(trackIdx + vc), [trackIdx, vc, goTo]);
  const prev = useCallback(() => goTo(trackIdx - vc), [trackIdx, vc, goTo]);

  /* ── After each animated transition, check if we're on a clone
        and silently jump to the corresponding real slide ─────────── */
  const onTransitionEnd = useCallback(() => {
    const total = N + 2 * vc;

    if (trackIdx >= N + vc) {
      // Past last real slide → jump to first real slide
      setAnimated(false);
      setTrackIdx(trackIdx - N);
    } else if (trackIdx < vc) {
      // Before first real slide → jump to last real slide
      setAnimated(false);
      setTrackIdx(trackIdx + N);
    }
  }, [trackIdx, vc]);

  /* ── Auto-play every 4 s ─────────────────────────────────────── */
  const resetTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTrackIdx(t => {
        setAnimated(true);
        return t + vc;
      });
    }, 4000);
  }, [vc]);

  useEffect(() => {
    if (vc === 0 || cw === 0) return;
    resetTimer();
    return () => clearInterval(timerRef.current);
  }, [vc, cw, resetTimer]);

  /* ── Drag / swipe ────────────────────────────────────────────── */
  const ds = (e) => {
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    drag.current = { on: true, x0: x, x1: x };
    setDragging(true);
    clearInterval(timerRef.current); // pause autoplay while dragging
  };
  const dm = (e) => {
    if (!drag.current.on) return;
    drag.current.x1 = e.touches ? e.touches[0].clientX : e.clientX;
  };
  const de = () => {
    if (!drag.current.on) return;
    drag.current.on = false;
    setDragging(false);
    const d = drag.current.x0 - drag.current.x1;
    if (d > 45)       { next(); resetTimer(); }
    else if (d < -45) { prev(); resetTimer(); }
    else              resetTimer();
  };

  /* Dot click → navigate to start of that dot group */
  const goDot = (d) => {
    const target = vc + (d * vc);   // trackIdx of first real slide in that group
    goTo(target);
    resetTimer();
  };

  return (
    <>
      <style>{`
        .mhs, .mhs * { box-sizing: border-box; margin: 0; padding: 0; }

        .mhs {
          background: #efefef;
          font-family: Arial, 'Helvetica Neue', Helvetica, sans-serif;
          overflow: hidden;
          padding: 42px 0 52px;
        }

        /* Header */
        .mhs-hdr {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 22px;
        }
        .mhs-logo {
          height: clamp(22px, 2.3vw, 38px);
          width: auto;
          display: block;
          margin-bottom: 7px;
        }
        .mhs-sub {
          font-size: clamp(13px, 1.1vw, 15px);
          color: #1a1a1a;
          font-weight: 400;
          letter-spacing: 0.01em;
          line-height: 1.4;
        }
        .mhs-dl {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: clamp(11px, 0.88vw, 13px);
          font-weight: 700;
          color: #c0392b;
          text-decoration: underline;
          text-underline-offset: 3px;
          letter-spacing: 0.05em;
          white-space: nowrap;
          cursor: pointer;
          transition: color 0.18s;
          line-height: 1;
        }
        .mhs-dl:hover { color: #9a0e1c; }

        /* Carousel */
        .mhs-shell { position: relative; }
        .mhs-az {
          position: absolute; top: 0; z-index: 20;
          display: flex; align-items: center;
        }
        .mhs-az-l { left: 0;  padding-left:  6px; }
        .mhs-az-r { right: 0; padding-right: 6px; }
        .mhs-clip { overflow: hidden; }
        .mhs-track { display: flex; align-items: flex-start; will-change: transform; }

        /* Card */
        .mhs-card { flex-shrink: 0; transition: opacity 0.33s ease; }
        .mhs-vbox {
          width: 100%; overflow: hidden;
          background: #111; border-radius: 8px; position: relative;
        }
        .mhs-info { padding: 13px 3px 0; }
        .mhs-ttl {
          font-size: clamp(14px, 1.4vw, 20px);
          font-style: italic; font-weight: 900;
          color: #111; letter-spacing: -0.015em;
          line-height: 1.2; margin-bottom: 4px;
        }
        .mhs-dsc {
          font-size: clamp(12px, 0.97vw, 14px);
          color: #555; font-weight: 400; line-height: 1.55;
        }

        /* Dots */
        .mhs-dots {
          display: flex; justify-content: center;
          align-items: center; gap: 8px; margin-top: 24px;
        }
        .mhs-dot {
          height: 4px; border-radius: 2px; border: none;
          padding: 0; cursor: pointer; outline: none;
          transition: width 0.3s ease, background 0.3s ease;
        }

        /* Responsive */
        @media (max-width: 767px) {
          .mhs { padding: 28px 0 40px; }
          .mhs-hdr { margin-bottom: 16px; }
          .mhs-vbox { border-radius: 6px; }
          .mhs-dots { margin-top: 18px; }
          .mhs-ttl { font-size: 15px; }
          .mhs-dsc { font-size: 12px; }
        }
        @media (min-width: 768px) and (max-width: 1199px) {
          .mhs { padding: 36px 0 48px; }
        }
      `}</style>

      <section ref={secRef} className="mhs">

        {/* ══ HEADER ══════════════════════════════ */}
        <div
          className="mhs-hdr"
          style={{ paddingLeft: pk || 16, paddingRight: pk || 16 }}
        >
          <div>
            <picture>
              <source
                media="(min-width:650px)"
                srcSet="https://mgmotor.scene7.com/is/image/mgmotor/poweredbymhubtitle-logo-018?fmt=webp-alpha&resMode=bisharp&fit=constrain&qlt=90&wid=1920"
              />
              <img
                loading="lazy" className="mhs-logo"
                src="https://mgmotor.scene7.com/is/image/mgmotor/poweredbymhubtitle-logo-018?fmt=webp-alpha&resMode=bisharp&fit=constrain&qlt=90&wid=720"
                alt="Powered by M-HUB"
              />
            </picture>
            <p className="mhs-sub">Conquer every obstacle with confidence.</p>
          </div>

          <a
            href="https://s7ap1.scene7.com/is/content/mgmotor/mgmotor/documents/MG%20Majestor%20-%20Brochure.pdf"
            target="_blank" rel="noopener noreferrer" className="mhs-dl"
          >
            Download Brochure
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="7" y1="17" x2="17" y2="7" />
              <polyline points="7 7 17 7 17 17" />
            </svg>
          </a>
        </div>

        {/* ══ CAROUSEL ════════════════════════════ */}
        <div className="mhs-shell">

          <div className="mhs-az mhs-az-l" style={{ height: videoH || "auto" }}>
            <Arr dir="prev" onClick={() => { prev(); resetTimer(); }} />
          </div>

          <div className="mhs-az mhs-az-r" style={{ height: videoH || "auto" }}>
            <Arr dir="next" onClick={() => { next(); resetTimer(); }} />
          </div>

          <div
            className="mhs-clip"
            onMouseDown={ds} onMouseMove={dm} onMouseUp={de} onMouseLeave={de}
            onTouchStart={ds} onTouchMove={dm} onTouchEnd={de}
          >
            <div
              className="mhs-track"
              style={{
                gap: `${GAP}px`,
                transform: `translateX(${tx}px)`,
                transition: animated && !dragging
                  ? "transform 0.52s cubic-bezier(0.25,0.46,0.45,0.94)"
                  : "none",
                cursor: dragging ? "grabbing" : "grab",
              }}
              onTransitionEnd={onTransitionEnd}
            >
              {extended.map((s, i) => {
                /*
                  Compute opacity:
                  - in-view: fully visible
                  - one before or after the view window: peek opacity
                  - everything else: nearly invisible
                */
                const inView = i >= trackIdx && i < trackIdx + vc;
                const peekL  = i === trackIdx - 1;
                const peekR  = i === trackIdx + vc;
                const opacity = inView ? 1 : (peekL || peekR) ? 0.44 : 0.08;

                return (
                  <div
                    key={`${s.id}-${i}`}
                    className="mhs-card"
                    style={{ width: cw, minWidth: cw, opacity }}
                  >
                    <div className="mhs-vbox" style={{ height: videoH || undefined }}>
                      {cw > 0 && <Vid slide={s} mob={mob} />}
                    </div>
                    <div className="mhs-info">
                      <h4 className="mhs-ttl">{s.title}</h4>
                      <p  className="mhs-dsc">{s.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ══ DOTS ════════════════════════════════ */}
        <div className="mhs-dots">
          {Array.from({ length: dotCount }).map((_, d) => (
            <button
              key={d}
              className="mhs-dot"
              onClick={() => goDot(d)}
              aria-label={`Slide group ${d + 1}`}
              style={{
                width:      activeDot === d ? 30 : 10,
                background: activeDot === d ? "#1a1a1a" : "#c0c0c0",
              }}
            />
          ))}
        </div>

      </section>
    </>
  );
}